import { Injectable } from '@angular/core';
import { Language } from '@model/language';
import { TranslateService } from '@ngx-translate/core';
import { LocalSettingsKey } from '@shared/types/local-settings';
import { ConfigsStorage } from '@storage/configs.storage';

@Injectable({
    providedIn: 'root'
})
export class TranslatorService {
    private availableLanguages: Language[] = [];
    private defaultLanguages: Language[] = [
        { code: 'tr', text: 'SELECT_TR' },
        { code: 'en', text: 'SELECT_EN' }
    ];

    constructor(
        public translateService: TranslateService,
        private configsStorage: ConfigsStorage
    ) { }

    initAvailableLanguages(): void {
        if ((this.availableLanguages || []).length > 0) {
            return;
        }
        const userLanguages = this.configsStorage.config.USER_LANGUAGES;
        if (userLanguages) {
            const splitValues = String(userLanguages).split(',');
            splitValues.forEach(v => this.availableLanguages.push({ code: v, text: 'SELECT_' + v.toUpperCase() }));
        } else {
            this.availableLanguages = this.getDefaultLanguages();
        }
    }

    getAvailableLanguages(): Language[] {
        this.initAvailableLanguages();  // initialize if it has not been initialized yet
        return this.availableLanguages;
    }

    getDefaultLanguages(): Language[] {
        return this.defaultLanguages;
    }

    isOneOfDefaultLanguages(language: string): boolean {
        return this.defaultLanguages.map(l => l.code).includes(language);
    }

    getUsersSelectedLanguage(): string {
        return localStorage.getItem(LocalSettingsKey.TRANSLATE);
    }

    setDefaultLanguage(): void {
        if (this.getUsersSelectedLanguage() === null) {
            localStorage.setItem(LocalSettingsKey.TRANSLATE, 'tr');
        }
        this.translateService.setDefaultLang(this.getUsersSelectedLanguage());
    }

    useLanguage(language: string): void {
        localStorage.setItem(LocalSettingsKey.TRANSLATE, language);
        this.translateService.use(language);
    }

    getLanguage(languageCode: string): Language {
        this.initAvailableLanguages(); // initialize if it has not been initialized yet
        return this.availableLanguages.find(language => languageCode === language.code);
    }

    getSelectedLanguage(): Language {
        this.initAvailableLanguages(); // initialize if it has not been initialized yet
        return this.availableLanguages.find(language => this.getUsersSelectedLanguage() === language.code);
    }

    getSSOLanguageForSelected(): string {
        const selectedLanguage = this.getUsersSelectedLanguage();
        if (selectedLanguage === null) {
            return 'tr';
        }
        if (this.isOneOfDefaultLanguages(selectedLanguage)) {
            return selectedLanguage;
        }
        return 'en';
    }

    setLanguageAfterLogout(): void {
        const selectedLanguage = this.getUsersSelectedLanguage();
        if (selectedLanguage === null) {
            return;
        }
        if (this.isOneOfDefaultLanguages(selectedLanguage)) {
            return;
        }
        this.useLanguage('en');
    }
}
